import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { PROFILE } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

const defaultColSpan = {
	md: 6
};

const IDVerifyModal = NiceModal.create(() => {
	const modal = useModal();
	const axios = useAxiosPrivate();
	const handleCloseModal = () => modal.remove();
	const { showSuccess, showError } = useAlert();
	const schema = useMemo(
		() =>
			yup.object().shape({
				firstName: yup.string().nullable().required(),
				lastName: yup.string().nullable().required(),
				identityNo: yup.string().nullable().min(11).max(11).required(),
				dateOfBirth: yup.string().nullable().required()
			}),
		[]
	);

	const user = useSelector((state) => state?.user);
	const dispatch = useDispatch();

	const fillForm = useCallback(async () => {
		const defaultValues = {
			firstName: null,
			lastName: null,
			identityNo: null,
			dateOfBirth: null
		};

		if (!user) {
			return defaultValues;
		}

		try {
			const { firstName = null, lastName = null, dateOfBirth = null, nationalID = null } = user;
			return {
				firstName,
				lastName,
				identityNo: nationalID,
				dateOfBirth
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [user]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		if (user.nationalIDVerified) {
			handleCloseModal();
		}

		try {
			const response = await axios.post(PROFILE.VERIFY_USER, {
				firstName: data.firstName,
				surname: data.lastName,
				dateOfBirth: data.dateOfBirth,
				identityNo: Number(data.identityNo)
			});
			const { status } = response;

			if (status === 200) {
				dispatch(
					setUserDetails({
						fullName: `${data.firstName} ${data.lastName}`,
						nationalIDVerified: true,
						dateOfBirth: data.dateOfBirth,
						nationalID: Number(data.identityNo)
					})
				);
				showSuccess();
				handleCloseModal();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error?.response.data.errorMessage || error?.response.data.code || error?.message
			});
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="id-verify-modal" closeButton={true}>
			<ModalTitle>
				<Typography variant="p1">verifyIdModalTitle</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="firstName" isLoading={isLoading} required>
						<StringInput name="firstName" maxLength="50" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="lastName" isLoading={isLoading} required>
						<StringInput name="lastName" maxLength="50" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="idNumber" isLoading={isLoading} required>
						<StringInput name="identityNo" maxlength={11} allowOnlyNumbers />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="dateOfBirth" isLoading={isLoading} required>
						<DateInput
							name="dateOfBirth"
							value={watch('dateOfBirth')}
							setValue={(value) => {
								setValue('dateOfBirth', value, { shouldValidate: true, shouldDirty: true });
							}}
							maxDate={new Date().toISOString()}
							withoutTime
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					label="verifyIdButtonText"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default IDVerifyModal;
